import apiClient from './apiClient';
import interviewService from './interviewService';
import {
  CadrageReadWithTeam,
  CadrageCreate,
  CadrageUpdate,
  BlockerRead,
  BlockerCreate,
  BlockerUpdate,
  User, // Assurez-vous que ce type est défini pour la gestion d'équipe
  Interview,
  UseCaseDetail
} from '../types'; // Ajustez le chemin si nécessaire

const apiBaseURL = '/cadrages/';

const cadrageService = {
  /**
   * Récupère la liste des cadrages pour l'utilisateur courant.
   */
  async getCadrages(): Promise<CadrageReadWithTeam[]> {
    const response = await apiClient.get<CadrageReadWithTeam[]>(apiBaseURL);
    return response.data;
  },

  /**
   * Récupère la liste des cadrages pour un cas d'usage spécifique.
   * @param useCaseId - L'ID du cas d'usage.
   */
  async getCadragesByUseCase(useCaseId: string): Promise<CadrageReadWithTeam[]> {
    const response = await apiClient.get<CadrageReadWithTeam[]>(apiBaseURL, {
      params: { use_case_id: useCaseId },
    });
    return response.data;
  },

  /**
   * Récupère un cadrage spécifique par son ID.
   * @param id - L'ID du cadrage.
   */
  async getCadrageById(id: string): Promise<CadrageReadWithTeam> {
    const response = await apiClient.get<CadrageReadWithTeam>(`${apiBaseURL}${id}`);
    return response.data;
  },

  /**
   * Récupère l'historique complet des versions d'un cadrage.
   * @param id - L'ID du cadrage.
   */
  async getCadrageHistory(id: string): Promise<CadrageReadWithTeam[]> {
    const response = await apiClient.get<CadrageReadWithTeam[]>(`${apiBaseURL}${id}/history`);
    return response.data;
  },

  /**
   * Crée une nouvelle version à partir de la version active d'un cadrage.
   * @param cadrageId - L'ID du cadrage actif à partir duquel créer une nouvelle version.
   */
  async createNewVersion(cadrageId: string): Promise<CadrageReadWithTeam> {
    const response = await apiClient.post<CadrageReadWithTeam>(
      `${apiBaseURL}${cadrageId}/create-version`
    );
    return response.data;
  },

  /**
   * Crée un nouveau cadrage.
   * @param data - Les données de création du cadrage.
   */
  async createCadrage(data: CadrageCreate): Promise<CadrageReadWithTeam> {
    const response = await apiClient.post<CadrageReadWithTeam>(apiBaseURL, data);
    return response.data;
  },

  /**
   * Met à jour un cadrage existant.
   * @param id - L'ID du cadrage à mettre à jour.
   * @param data - Les données de mise à jour.
   */
  async updateCadrage(id: string, data: CadrageUpdate): Promise<CadrageReadWithTeam> {
    const response = await apiClient.put<CadrageReadWithTeam>(`${apiBaseURL}${id}`, data);
    return response.data;
  },

  /**
   * Supprime un cadrage.
   * @param id - L'ID du cadrage à supprimer.
   */
  async deleteCadrage(id: string): Promise<void> {
    await apiClient.delete(`${apiBaseURL}${id}`);
  },

  /**
   * Ajoute un membre à l'équipe d'un cadrage.
   * @param cadrageId - L'ID du cadrage.
   * @param userId - L'ID de l'utilisateur à ajouter.
   */
  async addTeamMember(cadrageId: string, userId: string): Promise<CadrageReadWithTeam> {
    const response = await apiClient.post<CadrageReadWithTeam>(`${apiBaseURL}${cadrageId}/members/${userId}`);
    return response.data;
  },

  /**
   * Retire un membre de l'équipe d'un cadrage.
   * @param cadrageId - L'ID du cadrage.
   * @param userId - L'ID de l'utilisateur à retirer.
   */
  async removeTeamMember(cadrageId: string, userId: string): Promise<CadrageReadWithTeam> {
    const response = await apiClient.delete<CadrageReadWithTeam>(`${apiBaseURL}${cadrageId}/members/${userId}`);
    return response.data;
  },

  /**
   * Récupère la liste des verrous technologiques pour un cadrage.
   * @param cadrageId - L'ID du cadrage.
   */
  async getBlockers(cadrageId: string): Promise<BlockerRead[]> {
    const response = await apiClient.get<BlockerRead[]>(`${apiBaseURL}${cadrageId}/blockers/`);
    return response.data;
  },

  /**
   * Crée un nouveau verrou technologique pour un cadrage.
   * @param cadrageId - L'ID du cadrage.
   * @param data - Les données de création du verrou.
   */
  async createBlocker(cadrageId: string, data: BlockerCreate): Promise<BlockerRead> {
    const response = await apiClient.post<BlockerRead>(`${apiBaseURL}${cadrageId}/blockers/`, data);
    return response.data;
  },

  /**
   * Met à jour un verrou technologique existant.
   * @param cadrageId - L'ID du cadrage.
   * @param blockerId - L'ID du verrou à mettre à jour.
   * @param data - Les données de mise à jour.
   */
  async updateBlocker(cadrageId: string, blockerId: string, data: BlockerUpdate): Promise<BlockerRead> {
    const response = await apiClient.put<BlockerRead>(`${apiBaseURL}${cadrageId}/blockers/${blockerId}`, data);
    return response.data;
  },

  /**
   * Supprime un verrou technologique.
   * @param cadrageId - L'ID du cadrage.
   * @param blockerId - L'ID du verrou à supprimer.
   */
  async deleteBlocker(cadrageId: string, blockerId: string): Promise<void> {
    await apiClient.delete(`${apiBaseURL}${cadrageId}/blockers/${blockerId}`);
  },

  /**
   * Récupère le cas d'usage lié à un cadrage (s'il existe).
   * L'API retourne une liste, on prend le premier élément.
   * @param cadrageId - L'ID du cadrage.
   */
  async getLinkedUseCase(cadrageId: string): Promise<UseCaseDetail | null> { // Changer le type de retour
    try {
      // Assumer que l'API retourne bien un UseCaseDetail[] même si filtré
      const response = await apiClient.get<UseCaseDetail[]>(`/use-cases/?cadrage_id=${cadrageId}`);
      if (response.data && response.data.length > 0) {
        return response.data[0]; // Retourner le premier élément comme UseCaseDetail
      }
      return null;
    } catch (error) {
      console.error("Erreur lors de la récupération du Use Case lié:", error);
      // Gérer l'erreur comme approprié, par exemple retourner null ou lancer une erreur spécifique
      return null;
    }
  },

  // --- Fonctions utilitaires potentielles (si non gérées ailleurs) ---

  /**
   * Récupère la liste de tous les utilisateurs (pour l'ajout à l'équipe).
   * Note: Endpoint à confirmer ou à créer si nécessaire.
   */
  async getAllUsers(): Promise<User[]> {
     // Supposons un endpoint /users/ pour lister les utilisateurs
     // Adaptez l'endpoint si différent
    const response = await apiClient.get<User[]>('/users/');
    return response.data;
  },

  /**
   * Récupère la liste des entretiens pour un cadrage
   * @param cadrageId - L'ID du cadrage
   */
  async getInterviews(cadrageId: string): Promise<Interview[]> {
    return interviewService.getInterviews(undefined, cadrageId);
  },

  /**
   * Crée un dossier dans un cadrage
   * @param cadrageId - L'ID du cadrage
   * @param folderPath - Le chemin du dossier à créer
   */
  async createFolder(cadrageId: string, folderPath: string): Promise<void> {
    await apiClient.post(`${apiBaseURL}${cadrageId}/folders/`, { folder_path: folderPath });
  },

  /**
   * Supprime un dossier d'un cadrage
   * @param cadrageId - L'ID du cadrage
   * @param folderPath - Le chemin du dossier à supprimer
   */
  async deleteFolder(cadrageId: string, folderPath: string): Promise<void> {
    await apiClient.delete(`${apiBaseURL}${cadrageId}/folders/`, {
      data: { folder_path: folderPath }
    });
  },

  async getArchitectureFlow(cadrageId: string): Promise<any> {
    const response = await apiClient.get(`${apiBaseURL}${cadrageId}/architecture-flow/`);
    return response.data;
  },

  async listFolders(cadrageId: string, prefix?: string): Promise<any> {
    const params = prefix ? { prefix } : undefined;
    const response = await apiClient.get(`${apiBaseURL}${cadrageId}/folders/`, { params });
    return response.data;
  },

  async saveArchitectureFlow(cadrageId: string, payload: any): Promise<any> {
    const response = await apiClient.put(`${apiBaseURL}${cadrageId}/architecture-flow/`, payload);
    return response.data;
  },

};

export default cadrageService;
